// src/features/editor/coursesApi.ts
import { api } from '@/store/api'
import type { Course, CourseCreate, CourseUpdate, CourseWithoutBlocks } from '@/types/course'
import type { Lang } from '@/types/lang'
import { sortByOrder } from './lib/orderUtils'
import { readEditorCourse } from './lib/readEditorCourse'
import { dropCourseOptimistically } from './lib/dropCourseOptimistically'
import { fetchAllPages } from './lib/fetchAllPages'

/** Мгновенная правка кеша курса; отказ сервера откатывает её. */
const optimistic = async (
  courseId: number,
  recipe: (draft: CourseWithoutBlocks) => void,
  {
    dispatch,
    queryFulfilled,
  }: { dispatch: (a: never) => unknown; queryFulfilled: Promise<unknown> },
) => {
  const patch = dispatch(
    coursesApi.util.updateQueryData('getEditorCourse', courseId, recipe) as never,
  ) as { undo: () => void }
  try {
    await queryFulfilled
  } catch {
    patch.undo()
  }
}

export const coursesApi = api.injectEndpoints({
  endpoints: (build) => ({
    getCourse: build.query<CourseWithoutBlocks, number>({
      query: (id) => `gamified-courses/${id}`,
      transformResponse: (course: CourseWithoutBlocks) => ({
        ...course,
        lessons: sortByOrder(course.lessons),
      }),
      providesTags: (_r, _e, id) => [{ type: 'Course', id }],
    }),
    getEditorCourse: build.query<CourseWithoutBlocks, number>({
      // Источник чтения — ЧЕРНОВИК, если он есть (§3a): GET по живому id отдаёт живую
      // версию без правок. Ключ кеша — живой id: на нём держатся модерация, джобы, чат,
      // смета и все инвалидации редактора. Смену источника ловит ModerationPoller.
      queryFn: (liveId, api, _extra, baseQuery) => readEditorCourse(liveId, api, baseQuery),
      providesTags: (_r, _e, liveId) => [{ type: 'Course', id: liveId }],
    }),
    getMyUnpublishedCourses: build.query<Course[], void>({
      // Все страницы, а не первая: фильтры и счётчики считают по всем курсам автора.
      queryFn: (_arg, _api, _extra, baseQuery) =>
        fetchAllPages<Course>('authors/my-unpublished-courses', baseQuery),
      providesTags: [{ type: 'Course', id: 'LIST' }],
    }),
    getMyPublishedCourses: build.query<Course[], void>({
      queryFn: (_arg, _api, _extra, baseQuery) =>
        fetchAllPages<Course>('authors/my-published-courses', baseQuery),
      providesTags: [{ type: 'Course', id: 'LIST' }],
    }),
    createCourse: build.mutation<Course, CourseCreate>({
      query: (body) => ({ url: 'gamified-courses/', method: 'POST', body }),
      invalidatesTags: [{ type: 'Course', id: 'LIST' }],
    }),
    updateCourse: build.mutation<Course, { id: number; patch: CourseUpdate }>({
      query: ({ id, patch }) => ({ url: `gamified-courses/${id}`, method: 'PUT', body: patch }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Course', id }],
    }),
    deleteCourse: build.mutation<void, number>({
      query: (id) => ({ url: `gamified-courses/${id}`, method: 'DELETE' }),
      // Оптимистика: карточка пропадает со страницы «Мои курсы» сразу (см. хелпер).
      onQueryStarted: (id, lifecycle) => dropCourseOptimistically(id, lifecycle),
      invalidatesTags: (_r, _e, id) => [
        { type: 'Course', id },
        { type: 'Course', id: 'LIST' },
      ],
    }),
    /**
     * Публикация ОТДЕЛЬНОГО языка курса (§7.6). Не поле `PUT` курса, а своя
     * ручка, upsert публикации; объявить язык БЕЗ публикации —
     * `declareTranslation`. Язык-оригинал ею не публикуется
     * (`422 unsupported_language`) — он показывается общим `isPublished` курса.
     * Ответ — 204 без тела.
     */
    setTranslationPublished: build.mutation<
      void,
      { courseId: number; lang: Lang; isPublished: boolean }
    >({
      query: ({ courseId, lang, isPublished }) => ({
        url: `gamified-courses/${courseId}/translations/${lang}`,
        method: 'PUT',
        body: { isPublished },
      }),
      // Тумблер переключается сразу, не ждёт ответа и перечитывания курса;
      // отказ откатывает кеш.
      onQueryStarted: async ({ courseId, lang, isPublished }, { dispatch, queryFulfilled }) => {
        const patch = dispatch(
          coursesApi.util.updateQueryData('getEditorCourse', courseId, (draft) => {
            const rows = draft.translations ?? []
            const row = rows.find((r) => r.lang === lang)
            if (row) row.isPublished = isPublished
            else rows.push({ lang, isPublished })
            draft.translations = rows
          }),
        )
        try {
          await queryFulfilled
        } catch {
          patch.undo()
        }
      },
      // Меняется `publishedLangs` — протухают и деталь курса, и списки.
      invalidatesTags: (_r, _e, { courseId }) => [
        { type: 'Course', id: courseId },
        { type: 'Course', id: 'LIST' },
        { type: 'Quote', id: courseId },
      ],
    }),
    /**
     * Объявление языка перевода (§6.12 контракта api): заводит строку языка,
     * если её нет, и НИЧЕГО не меняет, если есть; публикацию не трогает
     * никогда. Не `setTranslationPublished({isPublished:false})`: тот —
     * upsert публикации и снял бы с каталога язык, заведённый через MCP или
     * второй вкладкой, а «Добавить» показывается, когда студия лишь ДУМАЕТ,
     * что строки нет. Бэк сбрасывает только свой кеш детали — деталь, списки
     * и смету (статус «Не переведён») перечитываем сами. 204 без тела.
     */
    declareTranslation: build.mutation<void, { courseId: number; lang: Lang }>({
      query: ({ courseId, lang }) => ({
        url: `gamified-courses/${courseId}/translations/${lang}`,
        method: 'POST',
      }),
      onQueryStarted: ({ courseId, lang }, api) =>
        optimistic(
          courseId,
          (d) => {
            const rows = d.translations ?? []
            if (!rows.some((r) => r.lang === lang)) rows.push({ lang, isPublished: false })
            d.translations = rows
          },
          api,
        ),
      invalidatesTags: (_r, _e, { courseId }) => [
        { type: 'Course', id: courseId },
        { type: 'Course', id: 'LIST' },
        { type: 'Quote', id: courseId },
      ],
    }),
    /**
     * Удаление языка перевода (§6.9). Сервер стирает второй слот курса, уроков
     * и блоков — поэтому протухают и уроки, и блоки, а не только курс; смета
     * тоже (статусы языков, `pending_units`). Ответ — 204 без тела.
     */
    deleteTranslation: build.mutation<void, { courseId: number; lang: Lang }>({
      query: ({ courseId, lang }) => ({
        url: `gamified-courses/${courseId}/translations/${lang}`,
        method: 'DELETE',
      }),
      onQueryStarted: ({ courseId, lang }, api) =>
        optimistic(
          courseId,
          (d) => {
            d.translations = (d.translations ?? []).filter((r) => r.lang !== lang)
          },
          api,
        ),
      invalidatesTags: (_r, _e, { courseId }) => [
        { type: 'Course', id: courseId },
        { type: 'Course', id: 'LIST' },
        { type: 'Lesson' },
        { type: 'Block' },
        { type: 'Quote', id: courseId },
      ],
    }),
    /**
     * Смена базового языка (§6.10) — тот же `PUT` курса, что и у формы
     * настроек, но отдельной ручкой: форма шлёт свои поля автосохранением и
     * живёт с глобальным тостом, а у этой два бизнес-отказа
     * (`422 translation_unpublished`, `409 studio_job_in_progress`), которые
     * объясняются по месту. Смета протухает: роли слотов поменялись.
     */
    changeSourceLang: build.mutation<Course, { courseId: number; sourceLang: Lang }>({
      query: ({ courseId, sourceLang }) => ({
        url: `gamified-courses/${courseId}`,
        method: 'PUT',
        body: { sourceLang },
      }),
      onQueryStarted: ({ courseId, sourceLang }, api) =>
        optimistic(
          courseId,
          (d) => {
            // Как бэк (§6.10): строка нового базового уходит. Строку прежнему базовому
            // не выдумываем: есть ли она и опубликована ли, знает только сервер, а
            // перечитывание после ответа всё поправит.
            d.sourceLang = sourceLang
            d.translations = (d.translations ?? []).filter((r) => r.lang !== sourceLang)
          },
          api,
        ),
      invalidatesTags: (_r, _e, { courseId }) => [
        { type: 'Course', id: courseId },
        { type: 'Course', id: 'LIST' },
        { type: 'Quote', id: courseId },
      ],
    }),
  }),
})

export const {
  useGetCourseQuery,
  useGetEditorCourseQuery,
  useGetMyUnpublishedCoursesQuery,
  useGetMyPublishedCoursesQuery,
  useCreateCourseMutation,
  useUpdateCourseMutation,
  useDeleteCourseMutation,
  useSetTranslationPublishedMutation,
  useDeclareTranslationMutation,
  useDeleteTranslationMutation,
  useChangeSourceLangMutation,
} = coursesApi
